import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, RowDragEndEvent, themeQuartz } from "ag-grid-community";
import { IEvent, IShow, IRunOrderClass, IDogRunOrderEntry, QueryDto } from "@eagerdog/interfaces";
import { Constants, getLevelsForDisplayName, isCwagsFeoEligible, isPerClassTimeslotShow, isUkcRallyShow, ukcRallyClassLabel } from "@eagerdog/constants";
import { apiService } from "src/services";
import { webSocketService } from "src/services/websocket.service";
import { localStorageService } from "src/services/local-storage.service";
import { Config } from "@eagerdog/config";
import { download } from "src/components/Download/DownloadManager";
import Dropdown from "src/components/Dropdown/Dropdown";
import Button from "src/components/Button/Button";
import Checkbox from "src/components/Checkbox/Checkbox";
import { processRunOrderData, compareByTimeSlotThenArmband } from "../../../../utils/runOrderDataProcessor";
import { calculateMPH, calculatePoints, formatToTwoDecimals } from "../../../../utils/fastCatCalculations";
import styles from "./RunOrderTab.module.scss";

// Simple debounce utility
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Default blind sizes per level / show type
const getDefaultBlindSize = (level: string, showType: string): number => {
  if (showType === Constants.show_type.urban_rat_race) return 5;
  if (showType === Constants.show_type.scent_dog_games) return 5;
  if (showType === Constants.show_type.barn_hunt) {
    if (level === Constants.dog_class_element_level.barn_hunt_master) return 4;
    if (
      [
        Constants.dog_class_element_level.barn_hunt_senior,
        Constants.dog_class_element_level.barn_hunt_crazy_8s,
      ].includes(level)
    )
      return 5;
    return 6;
  }
  return 5;
};

interface IProps {
  event: IEvent;
  show: IShow;
  onUpdate(updatedShow: IShow): void;
  onRegisterSync?(syncFn: () => void): void;
  onRegisterSetPending?(setPendingFn: (pending: boolean) => void): void;
  onManualSync?(): void;
  onRunOrderSave?(): void;
}

interface IGridRowData {
  id: string;
  classId: string;
  className: string;
  judgeName: string;
  armband: string;
  dogName: string;
  handlerName: string;
  breedName: string;
  breedGroup?: string; // Breed group for AKC Conformation
  scoreId: string;
  checkedIn: boolean;
  completed: boolean;
  originalLevel?: string; // Preserve original level for grouped class saves
  // AKC Conformation specific fields
  breedCounts?: string; // Format: "male-female-puppy-veteran"
  totalCount?: number;
  // Section for UKC Nosework and C-WAGS Obedience/Rally
  section?: string;
  // Jump height for Obedience and Rally Obedience
  jumpHeight?: number;
  // Show type specific result fields
  result?: string;
  time?: string;
  mph?: string;
  points?: string;
  score?: string;
  // Handler name for UKC Shed Dog and other shows with agent/handler
  agentName?: string;
  // Blind number for BHA Barn Hunt / ABI URR
  blindNumber?: number;
  // Time slot chosen at registration (General Obedience/Rally Show N Go)
  timeSlot?: string;
}

const RunOrderTab: React.FC<IProps> = React.memo((props) => {
  // Destructure props to avoid dependency issues
  const { event, show, onUpdate, onRegisterSync, onRegisterSetPending, onManualSync, onRunOrderSave } = props;
  // State management
  const [runOrderClasses, setRunOrderClasses] = useState<IRunOrderClass[]>([]);
  const [gridRowData, setGridRowData] = useState<IGridRowData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRing, setSelectedRing] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  const [dataVersion, setDataVersion] = useState<number>(0);
  const [judgeScheduleChangesPending, setJudgeScheduleChangesPending] = useState<boolean>(false);
  const [akcConformationCounts, setAkcConformationCounts] = useState<Map<string, any>>(new Map());
  const [isReversedOrder, setIsReversedOrder] = useState<boolean>(false);
  // Blind sizes per class (BHA Barn Hunt / ABI URR)
  const [blindSizes, setBlindSizes] = useState<Map<string, number>>(new Map());
  const [blindPanelCollapsed, setBlindPanelCollapsed] = useState<boolean>(false);

  const isBlindShow = useMemo(
    () =>
      (event.sanctioning_club === Constants.sanctioning_club.BHA &&
        show.show_type === Constants.show_type.barn_hunt) ||
      (event.sanctioning_club === Constants.sanctioning_club.ABI &&
        show.show_type === Constants.show_type.urban_rat_race) ||
      (event.sanctioning_club === Constants.sanctioning_club.ABI &&
        show.show_type === Constants.show_type.scent_dog_games),
    [event.sanctioning_club, show.show_type]
  );

  // General Obedience/Rally Show N Go: each dog picks a time slot per class, so dogs are
  // listed chronologically within each judge-schedule class rather than by armband.
  const isTimeslotOrderedShow = useMemo(
    () => isPerClassTimeslotShow(event.sanctioning_club, show.show_type),
    [event.sanctioning_club, show.show_type]
  );

  // Refs for persistent data storage and control
  const hasDataRef = useRef<boolean>(false);
  const runOrderClassesRef = useRef<IRunOrderClass[]>([]);
  const isolatedDataRef = useRef<IRunOrderClass[]>([]);
  const scoresRef = useRef<any[]>([]);
  const gridApiRef = useRef<AgGridReact | null>(null);
  const isCheckboxUpdateRef = useRef<boolean>(false);
  // Source-of-truth for checked-in state that bypasses AG-Grid's cell-refresh logic.
  // AG-Grid sometimes mutates params.data in place without triggering a React re-render,
  // which causes controlled checkbox inputs to revert. Reading from this ref (always current)
  // and calling refreshCells({ force: true }) ensures the DOM stays in sync.
  const checkedInMapRef = useRef<Map<string, boolean>>(new Map());
  const runOrderHashRef = useRef<string | null>(null);
  const isComponentMountedRef = useRef<boolean>(true);
  // Stable ref so onRowDragEnd (cross-split drag) always calls the latest move handler
  const moveDogToSplitRef = useRef<(scoreId: string, fromClassId: string, toClassId: string) => void>(() => {});

  // WebSocket connection for real-time updates
  useEffect(() => {
    const connectWebSocket = async () => {
      try {
        if (!show.show_run_order) return; // Only connect if run order is enabled
        
        const token = localStorageService.getItem(localStorageService.ACCESS_TOKEN);
        if (!token) return;

        await webSocketService.connect({
          url: Config.web.REACT_APP_API_URL || 'http://localhost:3001',
          namespace: '/run-order',
          token,
        });
      } catch (error) {
        // WebSocket connection failed
      }

      // Subscribe to run order updates
      const unsubscribe = webSocketService.onRunOrderUpdate((data) => {
        if (!isComponentMountedRef.current) return;
        
        // Check if update is for this show and ring
        if (data.showId === show._id && data.ring === selectedRing && data.reload) {
          // Reload data to reflect changes
          hasDataRef.current = false;
          setDataVersion(prev => prev + 1);
        }
      });

      return () => {
        unsubscribe();
      };
    };

    if (selectedRing && show.show_run_order) {
      connectWebSocket().then(cleanup => {
        if (cleanup) {
          // Store cleanup function
        }
      });
      
      // Join the room for this show/ring
      const roomData = {
        eventId: event._id,
        showId: show._id,
        ring: selectedRing,
      };
      webSocketService.joinRunOrderRoom(roomData);
    }

    return () => {
      // Leave room when component unmounts or ring changes
      if (selectedRing && show.show_run_order) {
        webSocketService.leaveRunOrderRoom();
      }
    };
  }, [event._id, show._id, show.show_run_order, selectedRing]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isComponentMountedRef.current = false;
    };
  }, []);

  const showRingsRef = useRef<any[]>([]); // Store the original rings structure for saving

  // AG-Grid theme - customized with orange colors
  const theme = themeQuartz.withParams({
    fontFamily: "Roboto",
    headerFontFamily: "Roboto",
    cellFontFamily: "Roboto",
    accentColor: "#ff6633", // Dark orange
    selectedRowBackgroundColor: "rgba(255, 102, 51, 0.1)",
    rangeSelectionBorderColor: "#ff6633"
  });

  // Fetch AKC Conformation counts if applicable
  useEffect(() => {
    if (event.sanctioning_club === Constants.sanctioning_club.AKC && 
        show.show_type === Constants.show_type.conformation) {
      apiService.getAKCConformationCounts(event._id, show._id).then((counts) => {
        if (!counts || !Array.isArray(counts)) {
          return;
        }
        
        const countsMap = new Map();
        counts.forEach(count => {
          if (!count || !count.breed_group || !count.breed) {
            return;
          }
          
          const key = `${count.breed_group}-${count.breed}`;
          if (!countsMap.has(key)) {
            countsMap.set(key, { 
              males: 0, females: 0, puppies: 0, veterans: 0,
              group_males: 0, group_females: 0, group_puppies: 0, group_veterans: 0
            });
          }
          const existing = countsMap.get(key);
          
          // Map AKC count fields to our display format
          existing.males += count.male_count || 0;
          existing.females += count.female_count || 0;
          existing.puppies += count.puppy_count || 0;
          existing.veterans += count.veteran_count || 0;
          
          // Also store group counts for breed group logic
          existing.group_males = (existing.group_males || 0) + (count.group_male_count || 0);
          existing.group_females = (existing.group_females || 0) + (count.group_female_count || 0);
          existing.group_puppies = (existing.group_puppies || 0) + (count.group_puppy_count || 0);
          existing.group_veterans = (existing.group_veterans || 0) + (count.group_veteran_count || 0);
        });
        
        setAkcConformationCounts(countsMap);
      }).catch((error) => {
        setAkcConformationCounts(new Map());
      });
    }
  }, [event._id, show._id, event.sanctioning_club, show.show_type]);

  // Helper function to check if a dog is complete based on score status
  const isDogComplete = useCallback((scoreId: string): boolean => {
    const score = scoresRef.current.find((s: any) => s._id === scoreId);
    return score?.status === 'complete' || score?.complete === true;
  }, []);

  // Handle checkbox change for dog checked-in status
  const handleCheckboxChange = useCallback((scoreId: string, classId: string, checkedIn: boolean) => {
    // Update the stable ref immediately so the cell renderer always reads the correct value.
    checkedInMapRef.current.set(scoreId, checkedIn);

    // Update grid data immediately for responsive UI
    setGridRowData(prevGridData => {
      return prevGridData.map(row => {
        if (row.scoreId === scoreId) {
          return {
            ...row,
            checkedIn: checkedIn,
            completed: isDogComplete(scoreId)
          };
        }
        return row;
      });
    });

    // Force AG-Grid to re-run the cell renderer so it reads the updated ref value.
    gridApiRef.current?.api?.refreshCells({ columns: ['checkedIn'], force: true });
    
    // Make the API call asynchronously without blocking the UI
    (async () => {
      try {
        await apiService.updateScoreCheckIn(event._id, scoreId, checkedIn);
        
        // Update the scores ref to reflect the change
        scoresRef.current = scoresRef.current.map((score: any) => {
          if (score._id === scoreId) {
            return { ...score, checked_in: checkedIn };
          }
          return score;
        });
        
      } catch (error) {
        // Revert the UI change on error
        const revertedValue = !checkedIn;
        checkedInMapRef.current.set(scoreId, revertedValue);
        setGridRowData(prevGridData => {
          return prevGridData.map(row => {
            if (row.scoreId === scoreId) {
              return { ...row, checkedIn: revertedValue };
            }
            return row;
          });
        });
        gridApiRef.current?.api?.refreshCells({ columns: ['checkedIn'], force: true });
    
        // Show error message
        setError("Failed to update check-in status. Please try again.");
      }
    })();
  }, [event._id, isDogComplete]);

  // AG-Grid column definitions
  const columnDefs = useMemo<ColDef[]>(() => {
    const isAKCConformation = event.sanctioning_club === Constants.sanctioning_club.AKC && 
                              show.show_type === Constants.show_type.conformation;
    const isSpecialtyShow = isAKCConformation && show.specialty_breed && show.specialty_breed.trim() !== '';
    
    if (isAKCConformation && !isSpecialtyShow) {
      // AKC Conformation ALL-BREED columns: Breed Group -> Breed -> Counts
      return [
        {
          headerName: "Breed Group",
          field: "judgeName", // judgeName contains the breed group
          rowGroup: true,
          hide: true
        },
        {
          headerName: "Breed",
          field: "dogName", // dogName contains the breed name
          width: 250,
          cellStyle: { fontWeight: '500' } as any
        },
        {
          headerName: "Total",
          field: "totalCount",
          width: 120,
          cellStyle: { fontWeight: 'bold', color: '#ff6633', textAlign: 'center' } as any,
          cellRenderer: (params: any) => {
            // For group rows (breed groups), show the group total
            if (params.node.group) {
              // Calculate breed group total from all children
              let groupTotal = 0;
              
              if (params.node.childrenAfterGroup) {
                params.node.childrenAfterGroup.forEach((child: any) => {
                  if (child.data && child.data.totalCount) {
                    groupTotal += child.data.totalCount;
                  }
                });
              }
              
              return <span style={{ fontWeight: 'bold', color: '#ff6633' }}>{groupTotal}</span>;
            }
            
            // For regular rows (breeds), show the breed total
            return <span>{params.data.totalCount || 0}</span>;
          }
        },
        {
          headerName: "Males - Females - Puppies - Veterans",
          field: "handlerName", // handlerName contains the counts in format "M-F-P-V"
          width: 300,
          cellStyle: { textAlign: 'center' } as any,
          cellRenderer: (params: any) => {
            // For group rows (breed groups), show the group totals
            if (params.node.group) {
              let totalMales = 0, totalFemales = 0, totalPuppies = 0, totalVeterans = 0;
              
              if (params.node.childrenAfterGroup) {
                params.node.childrenAfterGroup.forEach((child: any) => {
                  if (child.data && child.data.handlerName) {
                    const [males, females, puppies, veterans] = child.data.handlerName.split('-').map(Number);
                    totalMales += males || 0;
                    totalFemales += females || 0;
                    totalPuppies += puppies || 0;
                    totalVeterans += veterans || 0;
                  }
                });
              }
              
              return <span style={{ fontWeight: 'bold' }}>{totalMales} - {totalFemales} - {totalPuppies} - {totalVeterans}</span>;
            }
            
            // For regular rows (breeds), show the counts
            return <span>{params.data.handlerName || '0-0-0-0'}</span>;
          }
        }
      ];
    } else {
      // Regular columns for other show types
      const columns = [
        {
          // Dedicated drag handle column. rowDrag: true scopes the drag listener to only
          // this cell, so clicks on other cells (checkboxes, buttons) are never intercepted.
          headerName: "",
          field: "id",
          width: 36,
          rowDrag: true,
          cellRenderer: () => <span style={{ color: '#bbb', fontSize: 16, cursor: 'grab', userSelect: 'none' }}>⠿</span>,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 } as any,
        },
        {
          headerName: "Checked-In",
          field: "checkedIn",
          width: 130,
          cellRenderer: (params: any) => {
            const scoreId = params.data?.scoreId;
            const classId = params.data?.classId;
            const isChecked = checkedInMapRef.current.has(scoreId)
              ? checkedInMapRef.current.get(scoreId)!
              : (params.data?.checkedIn ?? false);
            // Use onClick on a div instead of the Checkbox component's label→input
            // association. animateRows keeps old row DOM elements alive, creating duplicate
            // <input id=...> elements. The Checkbox <label htmlFor=...> triggers the ghost
            // element instead of the live one so onChange never fires after the first click.
            // We replicate the Checkbox markup exactly (hidden input + checkSquare sibling so
            // the CSS input:checked ~ .checkSquare rule still draws the custom checkmark),
            // but wrap it in a plain div with onClick instead of a label.
            return (
              <div
                className="Checkbox"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', cursor: 'pointer' }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCheckboxChange(scoreId, classId, !isChecked);
                }}
              >
                {/* pointerEvents:none on the label so clicks fall through to the parent
                    div's onClick rather than activating the implicit label→input association.
                    The <label> tag itself is kept so the Checkbox.scss rules still apply. */}
                <label className="check" style={{ display: 'flex', pointerEvents: 'none', margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    readOnly
                  />
                  <div className="checkSquare"></div>
                </label>
              </div>
            );
          },
          cellStyle: { 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          } as any
        },
        {
          headerName: "Class / Judge",
          field: "className",
          rowGroup: true,
          hide: true
        },
        {
          headerName: "✓",
          field: "completed",
          width: 70,
          cellRenderer: (params: any) => (
            <div className={styles.completedCheckboxContainer}>
              {params.data.completed ? (
                <span className={styles.completedCheckmark}>✓</span>
              ) : (
                <span className={styles.emptyCheckbox}></span>
              )}
            </div>
          ),
          cellStyle: { 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center'
          } as any
        },
        {
          headerName: "",
          field: "dragHandle",
          width: 50,
          cellRenderer: () => "⋮⋮",
          cellStyle: { 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            cursor: 'grab',
            color: '#999'
          } as any
        },
        // Blind number column — only inserted for BHA Barn Hunt / ABI URR
        ...(isBlindShow ? [{
          headerName: "Blind",
          field: "blindNumber",
          width: 100,
          cellStyle: {
            textAlign: "center",
            fontWeight: "bold",
            color: "#555",
          } as any,
        }] : []),
        // Time slot column — only for shows where each dog picks its own time (Show N Go)
        ...(isTimeslotOrderedShow ? [{
          headerName: "Time Slot",
          field: "timeSlot",
          width: 180,
          cellStyle: { fontWeight: 'bold' } as any,
        }] : []),
        {
          headerName: "Armband",
          field: "armband",
          width: 120,
          cellStyle: { fontWeight: 'bold', color: '#ff6633' } as any
        },
        {
          headerName: "Dog",
          field: "dogName",
          flex: 1,
          minWidth: 70
        },
        {
          headerName: "Breed",
          field: "breedName",
          flex: 1,
          minWidth: 180
        },
        {
          headerName: "Owner",
          field: "handlerName",
          flex: 1,
          minWidth: 180
        }
      ];

      // Add Handler column for UKC Shed Dog (after Owner)
      const isUkcShedDog = event.sanctioning_club === Constants.sanctioning_club.UKC && 
                           show.show_type === Constants.show_type.shed_dog;
      
      if (isUkcShedDog) {
        columns.push({
          headerName: "Handler",
          field: "agentName",
          flex: 1,
          minWidth: 180
        });
      }

      if (isCwagsFeoEligible(event.sanctioning_club, show.show_type)) {
        columns.push({
          headerName: "FEO",
          field: "section",
          width: 80,
          cellRenderer: (params: any) => {
            return <span>{params.data?.section === Constants.dog_class_section.feo ? "FEO" : ""}</span>;
          },
          cellStyle: { textAlign: "center" } as any,
        });
      }

      // Add jump height column for Obedience, Rally Obedience, and Agility (after Owner)
      const isObedienceRallyOrAgility = show.show_type === Constants.show_type.obedience || 
                                        show.show_type === Constants.show_type.rally_obedience ||
                                        show.show_type === Constants.show_type.agility;
      
      if (isObedienceRallyOrAgility) {
        columns.push({
          headerName: "Jump Height",
          field: "jumpHeight",
          width: 140,
          cellRenderer: (params: any) => {
            const height = params.data.jumpHeight;
            return <span>{height ? `${height}"` : ''}</span>;
          },
          cellStyle: { textAlign: 'center' } as any
        });
      }

      // Add Fast CAT specific columns
      const isAkcFastCat = event.sanctioning_club === Constants.sanctioning_club.AKC && 
                           show.show_type === Constants.show_type.fast_cat;
      
      if (isAkcFastCat) {
        columns.push(
          {
            headerName: "Result",
            field: "result",
            width: 100,
            cellRenderer: (params: any) => {
              const result = params.data.result;
              return <span className={result === 'Q' ? styles.qualifiedResult : styles.nonQualifiedResult}>{result || ''}</span>;
            },
            cellStyle: { textAlign: 'center' } as any
          },
          {
            headerName: "Time",
            field: "time",
            width: 120,
            cellRenderer: (params: any) => {
              const time = params.data.time;
              return <span>{time || ''}</span>;
            },
            cellStyle: { textAlign: 'center' } as any
          },
          {
            headerName: "MPH",
            field: "mph",
            width: 80,
            cellRenderer: (params: any) => {
              const mph = params.data.mph;
              return <span>{mph || ''}</span>;
            },
            cellStyle: { textAlign: 'center' } as any
          },
          {
            headerName: "Points",
            field: "points",
            width: 80,
            cellRenderer: (params: any) => {
              const points = params.data.points;
              return <span>{points || ''}</span>;
            },
            cellStyle: { textAlign: 'center' } as any
          }
        );
      }

      // Add Obedience specific columns
      const isAkcObedience = event.sanctioning_club === Constants.sanctioning_club.AKC && 
                             show.show_type === Constants.show_type.obedience;
      
      if (isAkcObedience) {
        columns.push(
          {
            headerName: "Result",
            field: "result",
            width: 100,
            cellRenderer: (params: any) => {
              const result = params.data.result;
              return <span className={result === 'Q' ? styles.qualifiedResult : styles.nonQualifiedResult}>{result || ''}</span>;
            },
            cellStyle: { textAlign: 'center' } as any
          },
          {
            headerName: "Score",
            field: "score",
            width: 100,
            cellRenderer: (params: any) => {
              const score = params.data.score;
              return <span>{score || ''}</span>;
            },
            cellStyle: { textAlign: 'center' } as any
          }
        );
      }

      // Add Rally Obedience specific columns
      const isAkcRally = event.sanctioning_club === Constants.sanctioning_club.AKC && 
                         show.show_type === Constants.show_type.rally_obedience;
      
      if (isAkcRally) {
        columns.push(
          {
            headerName: "Result",
            field: "result",
            width: 100,
            cellRenderer: (params: any) => {
              const result = params.data.result;
              return <span className={result === 'Q' ? styles.qualifiedResult : styles.nonQualifiedResult}>{result || ''}</span>;
            },
            cellStyle: { textAlign: 'center' } as any
          },
          {
            headerName: "Time",
            field: "time",
            width: 120,
            cellRenderer: (params: any) => {
              const time = params.data.time;
              return <span>{time || ''}</span>;
            },
            cellStyle: { textAlign: 'center' } as any
          },
          {
            headerName: "Score",
            field: "score",
            width: 100,
            cellRenderer: (params: any) => {
              const score = params.data.score;
              return <span>{score || ''}</span>;
            },
            cellStyle: { textAlign: 'center' } as any
          }
        );
      }

      return columns;
    }
  }, [handleCheckboxChange, event.sanctioning_club, show.show_type, isBlindShow, isTimeslotOrderedShow]);

  // Computed grid data with blind numbers injected for blind shows
  const gridRowDataWithBlinds = useMemo(() => {
    if (!isBlindShow || blindSizes.size === 0) return gridRowData;
    const classCounters = new Map<string, number>();
    return gridRowData.map((row) => {
      const count = classCounters.get(row.classId) || 0;
      const size = blindSizes.get(row.classId) || 5;
      classCounters.set(row.classId, count + 1);
      return { ...row, blindNumber: Math.ceil((count + 1) / size) };
    });
  }, [gridRowData, blindSizes, isBlindShow]);

  // Initialize blindSizes from loaded class data
  useEffect(() => {
    if (!isBlindShow || runOrderClasses.length === 0) return;
    setBlindSizes((prev) => {
      const next = new Map(prev);
      let changed = false;
      runOrderClasses.forEach((cls) => {
        if (!next.has(cls.id)) {
          next.set(
            cls.id,
            cls.blind_size ?? getDefaultBlindSize(cls.level, show.show_type)
          );
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [runOrderClasses, isBlindShow, show.show_type]);

  // Handle blind size change for a class
  const handleBlindSizeChange = useCallback(
    (classId: string, size: number) => {
      if (size < 1) return;
      setBlindSizes((prev) => {
        const next = new Map(prev);
        next.set(classId, size);
        return next;
      });
      setHasUnsavedChanges(true);
    },
    []
  );

  // Convert class data to grid row data
  const convertToGridData = useCallback((classes: IRunOrderClass[], scores?: any[]): IGridRowData[] => {
    const gridData: IGridRowData[] = [];
    
    // Check if this is AKC Conformation
    const isAKCConformation = event.sanctioning_club === Constants.sanctioning_club.AKC && 
                              show.show_type === Constants.show_type.conformation;
    
    // Check if this is a specialty show (has a specialty breed)
    const isSpecialtyShow = isAKCConformation && show.specialty_breed && show.specialty_breed.trim() !== '';
    
    if (isAKCConformation && !isSpecialtyShow) {
      // For AKC Conformation ALL-BREED shows, create hierarchical: Judge -> Breed -> Counts
      // We need to process the running order data and match it with AKC counts
      
      const judgeBreedMap = new Map<string, { breeds: Set<string>, totalCounts: { males: number, females: number, puppies: number, veterans: number } }>();
      
      // Process each class from the running order
      classes.forEach(cls => {
        const judgeName = cls.judgeName || 'Default';
        const level = cls.level || '';
        
        if (!judgeBreedMap.has(judgeName)) {
          judgeBreedMap.set(judgeName, { 
            breeds: new Set(), 
            totalCounts: { males: 0, females: 0, puppies: 0, veterans: 0 } 
          });
        }
        
        const judgeData = judgeBreedMap.get(judgeName)!;
        
        // Determine if this is a breed-specific entry, breed group entry, or Junior Showmanship
        if (cls.showElement === 'Junior Showmanship') {
          // Special handling for Junior Showmanship
          judgeData.breeds.add('Junior Showmanship');
        } else if (level.includes(' - ')) {
          // This is a breed-specific entry like "Foundation Stock Service (FSS) - Perro De Presa Canario"
          const breedName = level.split(' - ')[1];
          if (breedName) {
            judgeData.breeds.add(breedName);
          }
        } else {
          // This is a breed group entry like "Working", "Toy", etc.
          // We need to find all breeds in this group that have entries
          akcConformationCounts.forEach((counts, key) => {
            const [keyBreedGroup, keyBreed] = key.split('-');
            
            // If this breed group matches and has group counts > 0, add the breed
            if (level === keyBreedGroup && keyBreed && 
                ((counts.group_males || 0) + (counts.group_females || 0) + (counts.group_puppies || 0) + (counts.group_veterans || 0)) > 0) {
              judgeData.breeds.add(keyBreed);
            }
          });
        }
      });
      
      // Now create grid rows for each judge and their breeds
      judgeBreedMap.forEach((judgeData, judgeName) => {
        // Calculate judge totals
        let judgeTotalMales = 0, judgeTotalFemales = 0, judgeTotalPuppies = 0, judgeTotalVeterans = 0;
        
        judgeData.breeds.forEach(breedName => {
          // Find the counts for this breed
          let breedCounts = '0-0-0-0';
          let totalCount = 0;
          let breedGroup = '';
          
          // Look for matching counts in the map
          for (const [key, counts] of akcConformationCounts.entries()) {
            // Special handling for Junior Showmanship
            if (breedName === 'Junior Showmanship' && key === 'Junior Showmanship-Junior Showmanship') {
              const males = counts.males || 0;
              const females = counts.females || 0;
              const puppies = counts.puppies || 0;
              const veterans = counts.veterans || 0;
              
              breedCounts = `${males}-${females}-${puppies}-${veterans}`;
              totalCount = males + females + puppies + veterans;
              breedGroup = 'Junior Showmanship';
              
              judgeTotalMales += males;
              judgeTotalFemales += females;
              judgeTotalPuppies += puppies;
              judgeTotalVeterans += veterans;
              break;
            } else if (key.endsWith(`-${breedName}`)) {
              
              // Extract breed group from the key (format: "BreedGroup-BreedName")
              breedGroup = key.split('-')[0] || '';
              
              // Check if this judge handles this breed specifically or as part of a group
              const isBreedSpecificJudge = classes.some(cls => 
                cls.judgeName === judgeName && cls.level && cls.level.includes(breedName)
              );
              
              if (isBreedSpecificJudge) {
                // Use full breed counts for breed-specific judges
                const males = counts.males || 0;
                const females = counts.females || 0;
                const puppies = counts.puppies || 0;
                const veterans = counts.veterans || 0;
                
                breedCounts = `${males}-${females}-${puppies}-${veterans}`;
                totalCount = males + females + puppies + veterans;
                
                judgeTotalMales += males;
                judgeTotalFemales += females;
                judgeTotalPuppies += puppies;
                judgeTotalVeterans += veterans;
              } else {
                // Use group counts for breed group judges (excluding breeds with specific judges)
                const groupMales = counts.group_males || 0;
                const groupFemales = counts.group_females || 0;
                const groupPuppies = counts.group_puppies || 0;
                const groupVeterans = counts.group_veterans || 0;
                
                breedCounts = `${groupMales}-${groupFemales}-${groupPuppies}-${groupVeterans}`;
                totalCount = groupMales + groupFemales + groupPuppies + groupVeterans;
                
                judgeTotalMales += groupMales;
                judgeTotalFemales += groupFemales;
                judgeTotalPuppies += groupPuppies;
                judgeTotalVeterans += groupVeterans;
              }
              break;
            }
          }
          
          // Add breed row
          gridData.push({
            id: `${judgeName}-${breedName}`,
            classId: `${judgeName}-${breedName}`,
            className: judgeName,
            judgeName: judgeName,
            armband: '',
            dogName: breedName,
            handlerName: breedCounts,
            breedName: breedName,
            breedGroup: breedGroup,
            scoreId: `${judgeName}-${breedName}`,
            checkedIn: false,
            completed: false, // Will be determined by score status
            breedCounts: breedCounts,
            totalCount: totalCount
          });
        });
        
        // Store judge totals for display in the group header
        judgeData.totalCounts = {
          males: judgeTotalMales,
          females: judgeTotalFemales,
          puppies: judgeTotalPuppies,
          veterans: judgeTotalVeterans
        };
      });
    } else {
      // Regular processing for non-AKC Conformation
      classes.forEach(cls => {
        cls.dogs.forEach((dog, index) => {
          // Find score data for Fast CAT calculations
          let result = '';
          let time = '';
          let mph = '';
          let points = '';
          let score = '';
          
          if (event.sanctioning_club === Constants.sanctioning_club.AKC && 
              show.show_type === Constants.show_type.fast_cat && 
              scores) {
            const scoreData = scores.find((score: any) => score._id === dog.scoreId);
            if (scoreData?.fastcat) {
              // Format result
              if (scoreData.fastcat.result_type) {
                result = scoreData.fastcat.result_type === Constants.score_result_type.qualified ? 'Q' : 'NQ';
              }
              
              // Format time and calculate MPH/Points
              if (scoreData.fastcat.time) {
                time = `${scoreData.fastcat.time.toFixed(2)}s`;
                
                // Calculate MPH and Points if we have height at withers
                if (scoreData.height_at_withers) {
                  const calculatedMPH = calculateMPH(scoreData.fastcat.time);
                  const calculatedPoints = calculatePoints(scoreData.fastcat.time, scoreData.height_at_withers);
                  mph = formatToTwoDecimals(calculatedMPH);
                  points = formatToTwoDecimals(calculatedPoints);
                }
              }
            }
          }

          // Add Obedience results
          if (event.sanctioning_club === Constants.sanctioning_club.AKC && 
              show.show_type === Constants.show_type.obedience && 
              scores) {
            const scoreData = scores.find((score: any) => score._id === dog.scoreId);
            if (scoreData?.obedience) {
              // Format result
              if (scoreData.obedience.result_type) {
                result = scoreData.obedience.result_type === Constants.score_result_type.qualified ? 'Q' : 'NQ';
              }
              
              // Calculate score (base 200 for obedience)
              if (scoreData.obedience.result_type === Constants.score_result_type.qualified) {
                let deductions = 0;
                if (scoreData.obedience.total_deductions_only) {
                  deductions = scoreData.obedience.total_deductions ?? 0;
                } else {
                  deductions = scoreData.obedience.element_deductions?.reduce((sum: number, item: any) => 
                    sum + parseInt(item.element_deductions ?? '0'), 0) ?? 0;
                }
                const finalScore = Math.max(200 - deductions, 0);
                score = finalScore.toString();
              } else {
                score = 'N/A';
              }
            }
          }

          // Add Rally Obedience results
          if (event.sanctioning_club === Constants.sanctioning_club.AKC && 
              show.show_type === Constants.show_type.rally_obedience && 
              scores) {
            const scoreData = scores.find((score: any) => score._id === dog.scoreId);
            if (scoreData?.rally_obedience) {
              // Format result
              if (scoreData.rally_obedience.result_type) {
                result = scoreData.rally_obedience.result_type === Constants.score_result_type.qualified ? 'Q' : 'NQ';
              }
              
              // Format time
              if (scoreData.rally_obedience.time) {
                time = `${scoreData.rally_obedience.time}`;
              }
              
              // Calculate score (base 100 for rally obedience)
              if (scoreData.rally_obedience.result_type === Constants.score_result_type.qualified) {
                let deductions = 0;
                if (scoreData.rally_obedience.total_deductions_only) {
                  deductions = scoreData.rally_obedience.total_deductions ?? 0;
                } else {
                  deductions = scoreData.rally_obedience.element_deductions?.reduce((sum: number, item: any) => 
                    sum + parseInt(item.element_deductions ?? '0'), 0) ?? 0;
                }
                const finalScore = Math.max(100 - deductions, 0);
                score = finalScore.toString();
              } else {
                score = 'N/A';
              }
            }
          }
          
          gridData.push({
            id: `${cls.id}-${dog.scoreId}`,
            classId: cls.id,
            className: event.sanctioning_club === Constants.sanctioning_club.AKC && show.show_type === Constants.show_type.fast_cat 
              ? `Timeslot: ${cls.judgeName}` 
              : event.sanctioning_club === Constants.sanctioning_club.BHA
              ? `${cls.level} - Judge: ${cls.judgeName}`
              : event.sanctioning_club === Constants.sanctioning_club.AKC && 
                (show.show_type === Constants.show_type.obedience || show.show_type === Constants.show_type.rally_obedience)
              ? `${cls.level} - Judge: ${cls.judgeName}`
              : event.sanctioning_club === Constants.sanctioning_club.UKC && show.show_type === Constants.show_type.agility
              ? `${cls.level} - Judge: ${cls.judgeName}`
              : event.sanctioning_club === Constants.sanctioning_club.ABI && show.show_type === Constants.show_type.scent_dog_games
              ? `${cls.level}${cls.section ? ` - ${cls.section}` : ''} - Judge: ${cls.judgeName}`
              : isUkcRallyShow(event.sanctioning_club, show.show_type)
              ? `${cls.showElement} - ${ukcRallyClassLabel(cls.level, cls.section)} - Judge: ${cls.judgeName}`
              : cls.section && cls.section !== "None" && cls.section !== cls.level && (event.sanctioning_club !== Constants.sanctioning_club.AKC || show.show_type === Constants.show_type.herding)
              ? `${cls.showElement} - ${cls.level} - ${cls.section} - Judge: ${cls.judgeName}`
              : `${cls.showElement} - ${cls.level} - Judge: ${cls.judgeName}`,
            judgeName: event.sanctioning_club === Constants.sanctioning_club.AKC && show.show_type === Constants.show_type.fast_cat 
              ? `Timeslot: ${cls.judgeName}` 
              : `Judge: ${cls.judgeName}`, // Keep for backward compatibility
            armband: dog.armband,
            dogName: dog.dogName,
            handlerName: dog.handlerName,
            breedName: dog.breedName,
            scoreId: dog.scoreId,
            checkedIn: scores?.find((s: any) => s._id === dog.scoreId)?.checked_in || false,
            completed: (() => { const s = scores?.find((s: any) => s._id === dog.scoreId); return s?.status === 'complete' || s?.complete === true; })(),
            originalLevel: dog.originalLevel, // Preserve originalLevel for grouped class saves
            section: dog.section, // Include section for UKC Nosework and C-WAGS Obedience/Rally
            jumpHeight: dog.jumpHeight, // Include jump height for Obedience and Rally Obedience
            agentName: dog.agentName, // Agent/Handler name for UKC Shed Dog
            timeSlot: dog.timeSlot, // Time slot chosen at registration (Show N Go)
            // Show type specific result fields
            result,
            time,
            mph,
            points,
            score
          });
        });
      });
    }
    
    return gridData;
  }, [event.sanctioning_club, show.show_type, akcConformationCounts, isDogComplete]);

  // Get available rings
  const availableRings = useMemo(() => {
    
    if (!show?.rings || show.rings.length === 0) {
      return [{ id: "ring1", label: "Ring 1", value: "Ring 1" }];
    }
    
    // With more than one ring, show each ring's entry count so a secretary looking at
    // Ring 1 (e.g. Sweepstakes only) can see the regular classes live in Ring 2.
    const showCounts = show.rings.length > 1;
    const rings = show.rings.map((ring, index) => {
      const entries = (ring.running_order || []).reduce((sum, ro) => {
        return sum + (ro.dog_classes || []).reduce((classSum, dc) => {
          if (dc.type !== Constants.running_order_dog_class_type.dog_class || (dc as any).group_with) {
            return classSum;
          }
          return classSum + (dc.entries || 0);
        }, 0);
      }, 0);

      return {
        id: `ring${index}`,
        label: showCounts ? `${ring.ring} (${entries} ${entries === 1 ? "entry" : "entries"})` : ring.ring,
        value: ring.ring
      };
    });
    
    return rings;
  }, [show?.rings]);

  // Set default ring
  useEffect(() => {
    if (!selectedRing && availableRings.length > 0) {
      setSelectedRing(availableRings[0].value);
    }
  }, [availableRings, selectedRing]);

  // Sync show prop changes to showRingsRef (e.g., when JudgeScheduleTab updates)
  // This ensures we always save with the latest rings structure
  useEffect(() => {
    if (show.rings && show.rings.length > 0) {
      showRingsRef.current = JSON.parse(JSON.stringify(show.rings));
    }
  }, [show]);


  // Load run order data
  const loadRunOrderData = useCallback(async (forceReload = false) => {
    if (!forceReload && (loading || !selectedRing)) {
      return;
    }
    
    if (!selectedRing) {

      return;
    }


    setLoading(true);
    setError(null);

    try {
      // Fetch fresh show data from the database to get the latest running_order
      // This ensures we see any changes made in the Judge Schedule tab
      const freshShows = await apiService.getClubEventShows(event.club_id, event._id);
      const freshShow = freshShows.find(s => s._id === show._id);
      
      // Use fresh show data if available, otherwise fall back to prop
      const currentShow = freshShow || show;
      
      // Check if this is AKC Conformation
      const isAKCConformation = event.sanctioning_club === Constants.sanctioning_club.AKC && 
                                currentShow.show_type === Constants.show_type.conformation;
      const isSpecialtyShow = isAKCConformation && currentShow.specialty_breed && currentShow.specialty_breed.trim() !== '';
      
      if (isAKCConformation && !isSpecialtyShow) {
        // For AKC Conformation ALL-BREED shows, fetch scores and create classes based on actual registrations
        const allScores = await apiService.getEventScores(event._id, {
          show_id: currentShow._id
        } as QueryDto);
        
        // Filter scores for the selected ring
        const ringScores = allScores.filter(score => {
          // For all-breed shows, we may not have ring assignments yet, so include all scores
          return true;
        });
        
        // Group scores by breed group and breed
        const breedGroupMap = new Map<string, Map<string, any[]>>();
        
        ringScores.forEach(score => {
          const breedGroup = score.breed_group || 'Unknown Group';
          const breed = score.breed || 'Unknown Breed';
          
          if (!breedGroupMap.has(breedGroup)) {
            breedGroupMap.set(breedGroup, new Map());
          }
          
          const breedsInGroup = breedGroupMap.get(breedGroup)!;
          if (!breedsInGroup.has(breed)) {
            breedsInGroup.set(breed, []);
          }
          
          breedsInGroup.get(breed)!.push(score);
        });
        
        // Create run order classes from breed groups
        const processedClasses: IRunOrderClass[] = [];
        
        breedGroupMap.forEach((breeds, breedGroup) => {
          breeds.forEach((scores, breed) => {
            processedClasses.push({
              id: `${breedGroup}-${breed}`,
              showElement: 'Conformation',
              level: breed, // Use breed as the level for all-breed shows
              judgeName: breedGroup, // Use breed group as the judge name for grouping
              dogs: scores.map(score => ({
                scoreId: score._id,
                armband: score.arm_number || '',
                dogName: score.call_name || score.registered_name || '',
                handlerName: score.owner_name || '',
                breedName: score.breed || '',
                originalLevel: score.dog_class?.level || ''
              })),
              originalDogClass: scores[0]?.dog_class || null,
              completedDogs: []
            });
          });
        });
        
        setRunOrderClasses(processedClasses);
        
        // For all-breed shows, build gridRowData directly without using convertToGridData
        // since the structure is different (grouped by breed group, then breed)
        const gridData: IGridRowData[] = [];
        
        processedClasses.forEach(cls => {
          const breedGroup = cls.judgeName; // We stored breed group as judgeName
          const breed = cls.level; // We stored breed as level
          const totalDogs = cls.dogs.length;
          
          // Find the counts for this breed from akcConformationCounts
          const countsKey = `${breedGroup}-${breed}`;
          const counts = akcConformationCounts.get(countsKey);
          const males = counts?.males || totalDogs; // Fallback to totalDogs if no counts
          const females = counts?.females || 0;
          const puppies = counts?.puppies || 0;
          const veterans = counts?.veterans || 0;
          
          gridData.push({
            id: cls.id,
            classId: cls.id,
            className: `${breedGroup} - ${breed}`,
            judgeName: breedGroup,
            armband: '',
            dogName: breed,
            handlerName: `${males}-${females}-${puppies}-${veterans}`,
            breedName: breed,
            breedGroup: breedGroup,
            scoreId: cls.id,
            checkedIn: false,
            completed: false,
            totalCount: totalDogs
          });
        });
        
        setGridRowData(gridData);
        runOrderClassesRef.current = processedClasses;
        isolatedDataRef.current = [...processedClasses];
        scoresRef.current = ringScores;
        hasDataRef.current = true;
        
        // Store the rings structure for later use in saveRunOrder
        if (show.rings && show.rings.length > 0) {
          showRingsRef.current = JSON.parse(JSON.stringify(show.rings));
        }
        setDataVersion(prev => prev + 1);
        setLoading(false);
        return;
      } else if (isAKCConformation && isSpecialtyShow) {
        // For AKC Conformation SPECIALTY shows, use the shared utility (running_order structure)
        const allScores = await apiService.getEventScores(event._id, {
          show_id: show._id
        } as QueryDto);

        const { processedClasses } = processRunOrderData({
          selectedShow: currentShow,
          selectedRing,
          allScores,
          event: event,
        });
        setRunOrderClasses(processedClasses);
        const gridData = convertToGridData(processedClasses, allScores);
        setGridRowData(gridData);
        // Sync checked-in ref from fresh scores so the cell renderer starts with correct values
        const newCheckedInMap = new Map<string, boolean>();
        allScores.forEach((s: any) => { newCheckedInMap.set(s._id?.toString(), !!s.checked_in); });
        checkedInMapRef.current = newCheckedInMap;
        runOrderClassesRef.current = processedClasses;
        isolatedDataRef.current = [...processedClasses];
        scoresRef.current = allScores;
        hasDataRef.current = true;
        
        // Store the rings structure for later use in saveRunOrder
        if (currentShow.rings && currentShow.rings.length > 0) {
          showRingsRef.current = JSON.parse(JSON.stringify(currentShow.rings));
        }
        setDataVersion(prev => prev + 1);
        setLoading(false);
      } else {
        // For non-AKC Conformation, fetch scores and use the shared utility
        const allScores = await apiService.getEventScores(event._id, {
          show_id: currentShow._id
        } as QueryDto);

        const { processedClasses } = processRunOrderData({
          selectedShow: currentShow,
          selectedRing,
          allScores,
          event: event, // Pass event for Fast CAT timeslot grouping
        });
        
        // Update state and refs for non-AKC Conformation
        setRunOrderClasses(processedClasses);
        // Sync checked-in ref from fresh scores
        const newCheckedInMap = new Map<string, boolean>();
        allScores.forEach((s: any) => { newCheckedInMap.set(s._id?.toString(), !!s.checked_in); });
        checkedInMapRef.current = newCheckedInMap;
        // Skip convertToGridData for AKC Conformation ALL-BREED shows (not specialty)
        const isAKCConformation = event.sanctioning_club === Constants.sanctioning_club.AKC && 
                                  currentShow.show_type === Constants.show_type.conformation;
        const isSpecialtyShow = isAKCConformation && currentShow.specialty_breed && currentShow.specialty_breed.trim() !== '';
        
        if (!isAKCConformation || isSpecialtyShow) {
          const gridData = convertToGridData(processedClasses, allScores);
        setGridRowData(gridData);
        }
        runOrderClassesRef.current = processedClasses;
        isolatedDataRef.current = [...processedClasses];
        scoresRef.current = allScores; // Store scores for other functions
        hasDataRef.current = true;
        
        // Store the rings structure for later use in saveRunOrder
        if (currentShow.rings && currentShow.rings.length > 0) {
          showRingsRef.current = JSON.parse(JSON.stringify(currentShow.rings));
        }
        setDataVersion(prev => prev + 1);
        setLoading(false);
      }

    } catch (err) {
      setError("Failed to load run order data. Please try again.");
      setLoading(false);
    }
  }, [event, show, selectedRing, loading, convertToGridData, akcConformationCounts]);

  // Manual sync function for Judge Schedule changes
  const manualSync = useCallback(() => {

    loadRunOrderData(true);
    setJudgeScheduleChangesPending(false);
  }, [loadRunOrderData]);

  // Function to set pending changes state
  const setPendingChanges = useCallback((pending: boolean) => {
    setJudgeScheduleChangesPending(pending);
  }, []);

  // Register functions with parent
  useEffect(() => {
    if (onRegisterSync) {
      onRegisterSync(manualSync);
    }
  }, [onRegisterSync, manualSync]);

  useEffect(() => {
    if (onRegisterSetPending) {
      onRegisterSetPending(setPendingChanges);
    }
  }, [onRegisterSetPending, setPendingChanges]);

  // Sync gridRowData whenever runOrderClasses changes
  useEffect(() => {

    
    if (runOrderClasses.length > 0) {
      // Skip grid data conversion if this is just a checkbox update
      if (isCheckboxUpdateRef.current) {
        isCheckboxUpdateRef.current = false; // Reset the flag

        return;
      }
      

      // Skip convertToGridData for AKC Conformation ALL-BREED shows (not specialty)
      const isAKCConformation = event.sanctioning_club === Constants.sanctioning_club.AKC && 
                                show.show_type === Constants.show_type.conformation;
      const isSpecialtyShow = isAKCConformation && show.specialty_breed && show.specialty_breed.trim() !== '';

      // Skip regeneration if we're in the middle of a manual reorder (drag/drop)
      if (skipGridRegenRef.current) {
        skipGridRegenRef.current = false;
        return;
      }

      if (!isAKCConformation || isSpecialtyShow) {
        const newGridData = convertToGridData(runOrderClasses, scoresRef.current);
      setGridRowData(newGridData);
    }
    }
  }, [runOrderClasses, convertToGridData, event.sanctioning_club, show.show_type, show.specialty_breed]);

  // Load data when component mounts or ring changes
  useEffect(() => {
    if (!loading && !hasDataRef.current && selectedRing) {
      loadRunOrderData();
    }
  }, [selectedRing, dataVersion, loadRunOrderData, loading, event.sanctioning_club, show.show_type]);

  // Reload data when show.rings changes (from Judge Schedule updates)
  // Use stringified comparison to avoid infinite loops from object reference changes
  const ringsStringRef = useRef<string>('');
  useEffect(() => {
    if (!hasDataRef.current || !selectedRing || !show.rings) return;
    
    const currentRingsString = JSON.stringify(show.rings);
    
    // Only reload if the rings data actually changed
    if (ringsStringRef.current && ringsStringRef.current !== currentRingsString) {
      loadRunOrderData(true); // Force reload
    }
    
    ringsStringRef.current = currentRingsString;
  }, [show.rings, selectedRing, loadRunOrderData]);

  // Track previous running order to detect actual changes
  const prevRunningOrderRef = useRef<string | null>(null);

  // React to changes in show data - automatically reload when running_order changes
  useEffect(() => {
    if (!selectedRing || !show.rings) return;
    
    // Get the running order for the selected ring
    const currentRing = show.rings.find(r => r.ring === selectedRing);
    if (!currentRing?.running_order) return;
    
    // Create a stable hash of the running order structure (class order, not dog order)
    // We only want to detect changes in the order of classes/judges, not individual dog order changes
    const runningOrderHash = JSON.stringify(currentRing.running_order.map(ro => ({
      judge_name: ro.judge_name,
      judge_number: ro.judge_number,
      dog_classes: ro.dog_classes?.map(dc => ({
        show_element: dc.show_element,
        level: dc.level,
        type: dc.type,
        sex_split: (dc as any).sex_split,
        variation: (dc as any).variation,
        group_with: (dc as any).group_with,
        group_order: (dc as any).group_order,
      })) || []
    })));
    
    // Only reload if the running order has actually changed (and we've loaded data before)
    if (prevRunningOrderRef.current !== null && 
        prevRunningOrderRef.current !== runningOrderHash && 
        hasDataRef.current) {
      // Running order structure changed, reload data
      hasDataRef.current = false;
      setJudgeScheduleChangesPending(false); // Clear the banner since we're auto-reloading
      loadRunOrderData(true);
    }
    
    // Update the ref with the current hash
    prevRunningOrderRef.current = runningOrderHash;
  }, [show.rings, selectedRing, loadRunOrderData]);

  // Handle ring change
  const handleRingChange = useCallback((e: any, option: any) => {
    const ringName = option?.value || "";

    setSelectedRing(ringName);
    setRunOrderClasses([]);
    setGridRowData([]);
    runOrderClassesRef.current = [];
    isolatedDataRef.current = [];
    hasDataRef.current = false;
    runOrderHashRef.current = null; // Reset hash for new ring
    prevRunningOrderRef.current = null; // Reset running order hash for new ring
    setError(null);
  }, []);

  // Handle show run order toggle
  const handleShowRunOrderToggle = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    
    try {
      setSaving(true);
      
      // Update the show with the new show_run_order value
      const showUpdateData = {
        show_name: show.show_name,
        show_type: show.show_type,
        show_date: show.show_date,
        fee: show.fee,
        use_early_bird_fee: show.use_early_bird_fee,
        use_same_fee: show.use_same_fee,
        use_registration_end_date: show.use_registration_end_date,
        show_elements: show.show_elements,
        rings: show.rings,
        show_run_order: newValue, // This is the key change
        // Include other optional fields if they exist
        ...(show.club_name && { club_name: show.club_name }),
        ...(show.show_club_id && { show_club_id: show.show_club_id }),
        ...(show.event_number && { event_number: show.event_number }),
        ...(show.trailing_show !== undefined && { trailing_show: show.trailing_show }),
        ...(show.previous_show_name && { previous_show_name: show.previous_show_name }),
        ...(show.registration_end_date && { registration_end_date: show.registration_end_date }),
        ...(show.early_bird_end_date && { early_bird_end_date: show.early_bird_end_date }),
        ...(show.early_bird_fee !== undefined && { early_bird_fee: show.early_bird_fee }),
        ...(show.is_archived !== undefined && { is_archived: show.is_archived }),
        ...(show.max_dogs !== undefined && { max_dogs: show.max_dogs }),
        ...(show.first_run_time_minutes !== undefined && { first_run_time_minutes: show.first_run_time_minutes }),
        ...(show.run_duration_minutes !== undefined && { run_duration_minutes: show.run_duration_minutes }),
        ...(show.breed_judges && { breed_judges: show.breed_judges }),
        ...(show.breed_group_judges && { breed_group_judges: show.breed_group_judges }),
        ...(show.arm_number_start !== undefined && { arm_number_start: show.arm_number_start }),
        ...(show.specialty_breed && { specialty_breed: show.specialty_breed }),
        ...(show.waitlist_option && { waitlist_option: show.waitlist_option }),
        ...(show.waitlist_limit !== undefined && { waitlist_limit: show.waitlist_limit }),
        ...(show.waitlist_enabled !== undefined && { waitlist_enabled: show.waitlist_enabled }),
        ...(show.waitlist_size !== undefined && { waitlist_size: show.waitlist_size }),
      };

      const updatedShow = await apiService.updateShow(event._id, show._id, showUpdateData);
      onUpdate(updatedShow);
      
    } catch (error: any) {
      setError("Failed to update run order visibility setting. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [event._id, show, onUpdate]);

  // Update class data from grid data
  const updateClassDataFromGrid = useCallback((gridData: IGridRowData[]) => {

    // Set flag to prevent useEffect from regenerating grid
    skipGridRegenRef.current = true;

    // Group grid data by class ID
    const classesByID = new Map<string, IGridRowData[]>();
    gridData.forEach(row => {
      if (!classesByID.has(row.classId)) {
        classesByID.set(row.classId, []);
      }
      classesByID.get(row.classId)!.push(row);
    });
    
    // Update existing classes using functional state update to get latest state
    setRunOrderClasses((currentClasses) => {
      const updatedClasses = currentClasses.map(cls => {
        const classRows = classesByID.get(cls.id) || [];
        const updatedDogs = classRows.map(row => {
          // Preserve all dog data, not just basic fields
          const originalDog = cls.dogs.find(d => d.scoreId === row.scoreId);
          return {
            ...originalDog, // Preserve all existing dog data
            scoreId: row.scoreId,
            armband: row.armband,
            dogName: row.dogName,
            handlerName: row.handlerName,
            breedName: row.breedName,
            originalLevel: row.originalLevel // Preserve originalLevel for grouped class saves
          };
        });
        
        return {
          ...cls,
          dogs: updatedDogs
        };
      });
      
      // Update refs
      runOrderClassesRef.current = updatedClasses;
      isolatedDataRef.current = [...updatedClasses];
      
      return updatedClasses;
    });

    
    // Update grid data to reflect the new order
    // Skip regeneration for all show types - the grid already has the correct data from the drag!
    // Regenerating would cause unnecessary re-renders and potential flickering
    
    // Mark as having unsaved changes instead of auto-saving
    setHasUnsavedChanges(true);
  }, [event.sanctioning_club, show.show_type, convertToGridData]);

  // Handle AG-Grid row drag end
  const onRowDragEnd = useCallback((event: RowDragEndEvent) => {
    const { node, overNode, api } = event;
    if (!overNode || !overNode.data) {
      return;
    }

    const draggedData = node.data as IGridRowData;
    const overData = overNode.data as IGridRowData;
    
    // Don't allow dropping on group rows
    if (overNode.group) return;

    // Cross-split drag: if both rows are in sibling split classes, move the dog
    if (draggedData.classId !== overData.classId) {
      const classes = runOrderClassesRef.current;
      const fromCls = classes.find(c => c.id === draggedData.classId);
      const toCls = classes.find(c => c.id === overData.classId);
      if (
        fromCls?.split_number !== undefined &&
        toCls?.split_number !== undefined &&
        fromCls.showElement === toCls.showElement &&
        fromCls.level.replace(/ \(Split \d+\/\d+\)$/, '') === toCls.level.replace(/ \(Split \d+\/\d+\)$/, '') &&
        fromCls.section === toCls.section
      ) {
        moveDogToSplitRef.current(draggedData.scoreId, draggedData.classId, overData.classId);
      }
      return;
    }
    
    // Get the current order from the grid before it updates
    const beforeRows: IGridRowData[] = [];
    api.forEachNode((node) => {
      if (node.data) {
        beforeRows.push(node.data);
      }
    });
    
    // Find indices for manual reordering
    const draggedIndex = beforeRows.findIndex(row => row.id === draggedData.id);
    const overIndex = beforeRows.findIndex(row => row.id === overData.id);
    
    if (draggedIndex === -1 || overIndex === -1) return;

    // If the row didn't actually move, do nothing. Without this guard, AG Grid fires
    // onRowDragEnd for simple clicks (rowDragEntireRow catches all mouse events) and
    // calling setGridRowData with stale api.forEachNode data would revert any checkbox
    // state update that handleCheckboxChange just issued.
    if (draggedIndex === overIndex) return;
    
    // Manually reorder the array
    const reorderedRows = [...beforeRows];
    const [draggedRow] = reorderedRows.splice(draggedIndex, 1);
    const newOverIndex = draggedIndex < overIndex ? overIndex - 1 : overIndex;
    reorderedRows.splice(newOverIndex, 0, draggedRow);
    
    // Update state and class data - React will handle updating AG-Grid
    // Do NOT call api.setGridOption here as it causes double updates and duplicates
    setGridRowData(reorderedRows);
    updateClassDataFromGrid(reorderedRows);
  }, [updateClassDataFromGrid]);



    // Handle reset to default order
  const handleReset = useCallback(() => {
    // Prevent rapid clicking while saving
    if (saving) return;
    
    const resetClasses = runOrderClasses.map(cls => ({
      ...cls,
      dogs: [...cls.dogs].sort((a: IDogRunOrderEntry, b: IDogRunOrderEntry) => {
        // Show N Go: dogs run in the time slot they picked at registration
        if (isTimeslotOrderedShow) {
          return compareByTimeSlotThenArmband(a, b);
        }

        // Sort by jump height first (if available)
        if (a.jumpHeight !== undefined && b.jumpHeight !== undefined) {
          if (a.jumpHeight !== b.jumpHeight) {
            return a.jumpHeight - b.jumpHeight;
          }
        }
        
        // Then sort by armband number (extract numeric part for comparison)
        const aNum = parseInt((a.armband || "0").replace(/[^0-9]/g, '')) || 0;
        const bNum = parseInt((b.armband || "0").replace(/[^0-9]/g, '')) || 0;
        
        if (aNum !== bNum) {
          return aNum - bNum;
        }
        
        // If same number, non-preferred comes before preferred
        const aHasP = (a.armband || "").startsWith('P');
        const bHasP = (b.armband || "").startsWith('P');
        
        if (!aHasP && bHasP) return -1;
        if (aHasP && !bHasP) return 1;
        
        return 0;
      })
    }));

    setRunOrderClasses(resetClasses);
    setIsReversedOrder(false); // Reset reverse state after reset to default
    
    // Skip convertToGridData for AKC Conformation to avoid overriding custom grid data
    const isAKCConformation = event.sanctioning_club === Constants.sanctioning_club.AKC && 
                              show.show_type === Constants.show_type.conformation;
    
    if (!isAKCConformation) {
      setGridRowData(convertToGridData(resetClasses, scoresRef.current));
    }
    runOrderClassesRef.current = resetClasses;
    isolatedDataRef.current = [...resetClasses];
    
    // Mark as having unsaved changes instead of auto-saving
    setHasUnsavedChanges(true);
  }, [runOrderClasses, convertToGridData, event.sanctioning_club, show.show_type, saving, isTimeslotOrderedShow]);

  // Handle randomize order within each level
  const handleRandomize = useCallback(() => {
    // Prevent rapid clicking while saving
    if (saving) return;
    
    const randomizedClasses = runOrderClasses.map(cls => ({
      ...cls,
      dogs: [...cls.dogs].sort(() => Math.random() - 0.5)
    }));

    setRunOrderClasses(randomizedClasses);
    setIsReversedOrder(false); // Reset reverse state after randomize
    
    // Skip convertToGridData for AKC Conformation to avoid overriding custom grid data
    const isAKCConformation = event.sanctioning_club === Constants.sanctioning_club.AKC && 
                              show.show_type === Constants.show_type.conformation;
    
    if (!isAKCConformation) {
      setGridRowData(convertToGridData(randomizedClasses, scoresRef.current));
    }
    runOrderClassesRef.current = randomizedClasses;
    isolatedDataRef.current = [...randomizedClasses];
    
    // Mark as having unsaved changes instead of auto-saving
    setHasUnsavedChanges(true);
  }, [runOrderClasses, convertToGridData, event.sanctioning_club, show.show_type, saving]);

  // Check if reverse order feature should be available (AKC/UKC/C-WAGS Rally and Obedience only)
  const shouldShowReverseOrder = useMemo(() => {
    const isAKCOrUKCOrCWAGS = event.sanctioning_club === Constants.sanctioning_club.AKC || 
                              event.sanctioning_club === Constants.sanctioning_club.UKC ||
                              event.sanctioning_club === Constants.sanctioning_club.C_WAGS;
    const isRallyObedienceOrAgility = show.show_type === Constants.show_type.rally_obedience || 
                                      show.show_type === Constants.show_type.obedience ||
                                      show.show_type === Constants.show_type.agility;
    return isAKCOrUKCOrCWAGS && isRallyObedienceOrAgility;
  }, [event.sanctioning_club, show.show_type]);

  // Handle reverse order within each level (toggle between normal and reversed armband order)
  const handleReverseOrder = useCallback(() => {
    // Prevent rapid clicking while saving
    if (saving) return;
    
    const newReversedState = !isReversedOrder;
    
    // Check if this is UKC Obedience/Rally, C-WAGS Obedience/Rally, or any Agility - they sort by jump height
    const sortByJumpHeight = ((event.sanctioning_club === Constants.sanctioning_club.UKC || 
                               event.sanctioning_club === Constants.sanctioning_club.C_WAGS) && 
                              (show.show_type === Constants.show_type.obedience || 
                               show.show_type === Constants.show_type.rally_obedience)) ||
                             show.show_type === Constants.show_type.agility;
    
    const sortedClasses = runOrderClasses.map(cls => ({
      ...cls,
      dogs: [...cls.dogs].sort((a, b) => {
        if (sortByJumpHeight) {
          // For UKC Obedience/Rally and Agility, sort by jump height
          const jumpHeightA = a.jumpHeight || 0;
          const jumpHeightB = b.jumpHeight || 0;
          
          // Toggle between ascending (normal, lowest to highest) and descending (reversed, highest to lowest) order
          return newReversedState ? jumpHeightB - jumpHeightA : jumpHeightA - jumpHeightB;
        } else {
          // For other show types (AKC), sort by armband
          const armbandA = parseInt(a.armband) || 0;
          const armbandB = parseInt(b.armband) || 0;
          
          // Toggle between ascending (normal) and descending (reversed) order
          return newReversedState ? armbandB - armbandA : armbandA - armbandB;
        }
      })
    }));

    setRunOrderClasses(sortedClasses);
    setIsReversedOrder(newReversedState);
    
    // Skip convertToGridData for AKC Conformation to avoid overriding custom grid data
    const isAKCConformation = event.sanctioning_club === Constants.sanctioning_club.AKC && 
                              show.show_type === Constants.show_type.conformation;
    
    if (!isAKCConformation) {
      setGridRowData(convertToGridData(sortedClasses, scoresRef.current));
    }
    runOrderClassesRef.current = sortedClasses;
    isolatedDataRef.current = [...sortedClasses];
    
    // Mark as having unsaved changes instead of auto-saving
    setHasUnsavedChanges(true);
  }, [runOrderClasses, isReversedOrder, convertToGridData, event.sanctioning_club, show.show_type, saving]);

  // Track unsaved changes (moved here to be available in saveRunOrder)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Flag to prevent useEffect from regenerating grid after manual reorder
  const skipGridRegenRef = useRef(false);

  // Auto-save function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const saveRunOrder = useCallback(async () => {

    
    if (!selectedRing) {

      return;
    }
    
    // Don't check saving state here since this is called by debounced function
    // The debounce handles preventing multiple simultaneous calls

    setSaving(true);
    try {
      // Find the current ring or create it
      // Use the stored rings ref (not show.rings prop which might be stale)
      // Deep clone to avoid mutating refs and ensure clean state for update
      const updatedRings = JSON.parse(JSON.stringify(showRingsRef.current.length > 0 ? showRingsRef.current : show.rings || []));
      let ringIndex = updatedRings.findIndex((r: any) => r.ring === selectedRing);
      
      if (ringIndex === -1) {
        // Create new ring
        updatedRings.push({
          ring: selectedRing,
          running_order: []
        });
        ringIndex = updatedRings.length - 1;
      }

      // Update the running order for each class
      const currentRingData = updatedRings[ringIndex];

      // Initialize running_order if it doesn't exist (e.g., for Fast CAT)
      if (!currentRingData.running_order) {
        currentRingData.running_order = [];
      }

      // For Fast CAT, we need to create/fix the running_order structure from the classes
      const isFastCat = show.show_type === Constants.show_type.fast_cat;
      if (isFastCat) {
        // Use ref to get the latest class data (state might be stale in closure)
        const currentClasses = runOrderClassesRef.current;
        
        // Check if running_order needs to be rebuilt
        // It needs rebuilding if: empty, missing time_slot fields, or judge_name doesn't match actual timeslots
        const needsRebuild = currentRingData.running_order.length === 0 ||
                            !currentRingData.running_order[0]?.dog_classes?.[0]?.time_slot ||
                            (currentRingData.running_order[0]?.judge_name === 'N/A' && currentClasses.length > 0 && currentClasses[0]?.judgeName !== 'N/A');
        
        if (needsRebuild) {
          // Rebuild the running_order structure from scratch using current timeslots
          const timeslotMap = new Map<string, IRunOrderClass[]>();
          currentClasses.forEach(cls => {
            const timeslot = cls.judgeName; // For Fast CAT, judgeName is the timeslot (Morning, Afternoon, etc.)
            if (!timeslotMap.has(timeslot)) {
              timeslotMap.set(timeslot, []);
            }
            timeslotMap.get(timeslot)!.push(cls);
          });

          // Clear and rebuild running_order
          currentRingData.running_order = [];
          
          // Create running_order entries for each timeslot
          timeslotMap.forEach((classes, timeslot) => {
            const dogClasses = classes.map(cls => ({
              type: 'dog_class', // Required field!
              show_element: cls.showElement,
              level: cls.level,
              time_slot: timeslot, // Add time_slot field
              dog_run_order: cls.dogs.map(dog => dog.scoreId),
              completed_dogs: [] // Initialize empty array
            }));

            currentRingData.running_order.push({
              judge_name: timeslot, // Use actual timeslot as dummy judge name
              judge_number: '',
              dog_classes: dogClasses
            });
          });
        }
      }
      
      // For UKC Agility, we need to create/fix the running_order structure from the grouped classes
      const isUkcAgility = event.sanctioning_club === Constants.sanctioning_club.UKC && 
                           show.show_type === Constants.show_type.agility;
      if (isUkcAgility) {
        const currentClasses = runOrderClassesRef.current;
        
        // Rebuild running_order for UKC Agility.
        // Group level-group classes by their actual judge name so each judge gets one entry.
        // Save ONE dog_class per level group with all dogs in their combined UI order
        // (interleaved by jump height across individual levels). Using a single combined
        // dog_run_order prevents the concatenation-order problem that occurs when splitting
        // by individual level (e.g., Beginner Standard + Beginner Premier interleaved dogs).
        currentRingData.running_order = [];
        
        const judgeMap = new Map<string, any[]>();
        currentClasses.forEach(cls => {
          // cls.level = level group display name (e.g., "Beginner")
          // cls.judgeName = actual judge name (e.g., "Dawn Fisher")
          if (!judgeMap.has(cls.judgeName)) {
            judgeMap.set(cls.judgeName, []);
          }
          // Use the first individual level as the representative level for the DC entry.
          // The forms-ms groups DCs by display name via getDisplayNameForLevel, so any
          // individual level within the group will map back to the correct group.
          const individualLevels = getLevelsForDisplayName(cls.level);
          const representativeLevel = individualLevels[0] || cls.level;
          judgeMap.get(cls.judgeName)!.push({
            type: 'dog_class',
            show_element: cls.showElement,
            level: representativeLevel,
            dog_run_order: cls.dogs.map(dog => dog.scoreId), // all dogs in interleaved UI order
            completed_dogs: []
          });
        });
        
        judgeMap.forEach((dogClasses, judgeName) => {
          currentRingData.running_order.push({
            judge_name: judgeName,
            judge_number: '',
            dog_classes: dogClasses
          });
        });
      }
      
      if (currentRingData.running_order) {
        // Use ref to get the latest class data (state might be stale in closure)
        const currentClasses = runOrderClassesRef.current;
        
        for (const cls of currentClasses) {
          // Find the corresponding running order item
          let matched = false;
          for (const runOrderItem of currentRingData.running_order) {
            if (runOrderItem.dog_classes) {
              for (const dc of runOrderItem.dog_classes) {
                // For Fast CAT, match by timeslot (stored in judgeName) and level
                const isFastCat = show.show_type === Constants.show_type.fast_cat;
                const fastCatMatch = isFastCat && 
                                    dc.time_slot === cls.judgeName && 
                                    dc.level === cls.level;
                
                // For Fast CAT, ONLY match by timeslot (don't use fallback condition)
                // For other shows, match by show_element, level, and section (if section exists)
                const sectionMatch = dc.section && cls.section ? dc.section === cls.section : true;
                // For split tiles, also match by split_number so sibling splits don't collide.
                // cls.originalDogClass carries split_number from the processor.
                const splitMatch = (dc as any).split_number !== undefined
                  ? (dc as any).split_number === cls.originalDogClass?.split_number
                  : cls.originalDogClass?.split_number === undefined;
                // For split tiles, cls.level has "(Split N/M)" appended for display purposes
                // (e.g. "Novice A (Split 1/2)"). The raw DB level is the original without the
                // suffix. Use originalDogClass.level for the comparison when available so split
                // tiles are matched correctly. Fall back to cls.level for non-split classes.
                // UKC Conformation A–Z breed group splits also carry a display suffix and
                // share a level, so they're told apart by their letter range.
                const original = cls.originalDogClass as any;
                const isLetterSplit = !!(original?.start_letter && original?.end_letter);
                const effectiveLevel = original?.split_number !== undefined || isLetterSplit
                  ? (original.level ?? cls.level)
                  : cls.level;
                const letterMatch = ((dc as any).start_letter || "") === (original?.start_letter || "") &&
                                    ((dc as any).end_letter || "") === (original?.end_letter || "");
                const normalMatch = !isFastCat && 
                                   dc.show_element === cls.showElement && 
                                   dc.level === effectiveLevel && 
                                   sectionMatch &&
                                   splitMatch &&
                                   letterMatch;
                
                if (fastCatMatch || normalMatch) {
                  // Update the dog_run_order with the new sequence
                  const newDogOrder = cls.dogs.map(dog => dog.scoreId);

                  dc.dog_run_order = newDogOrder;

                  // For split tiles, keep entries and total_minutes in sync so the
                  // Judge Schedule tile always shows the correct count after dogs are moved.
                  if ((dc as any).split_number !== undefined) {
                    const prevEntries: number = (dc as any).entries || 0;
                    const newEntries = newDogOrder.length;
                    (dc as any).entries = newEntries;
                    if (prevEntries > 0 && (dc as any).total_minutes) {
                      const durationPerEntry = (dc as any).total_minutes / prevEntries;
                      (dc as any).total_minutes = newEntries * durationPerEntry;
                    } else if ((dc as any).duration) {
                      (dc as any).total_minutes = newEntries * (dc as any).duration;
                    }
                  }

                  // Persist blind size for blind-enabled shows
                  if (isBlindShow) {
                    (dc as any).blind_size =
                      blindSizes.get(cls.id) ??
                      getDefaultBlindSize(cls.level, show.show_type);
                  }
                  matched = true;
                  
                  break;
                }
                // Check if this is a grouped class - the frontend class level contains "/"
                else if (cls.level.includes(' / ') && dc.show_element === cls.showElement) {
                  // Split the grouped level to get individual levels
                  const individualLevels = cls.level.split(' / ').map(level => level.trim());
                  
                  // Save the grouped class order to the parent class for inter-class positioning
                  if (dc.level === individualLevels[0]) {
                    // This is the parent class - save the complete grouped order
                    const groupedOrder = cls.dogs.map(dog => dog.scoreId);
                    dc.dog_run_order = groupedOrder;

                    // Skip individual processing for parent class since we saved master order
                    continue;
                  }
                  
                  // Check if this backend class matches any of the individual levels
                  // OR if any dogs in the frontend class have this backend level as their originalLevel
                  const hasDogsForThisLevel = cls.dogs.some(dog => dog.originalLevel === dc.level);

                  
                  if (dc.level && (individualLevels.includes(dc.level) || hasDogsForThisLevel)) {

                    
                    // For grouped classes, filter dogs to only include 
                    // those that belong to this specific individual class level
                    const dogsForThisLevel = cls.dogs.filter(dog => {
                      // Check if this dog's original class level matches the current backend level
                      return dog.originalLevel === dc.level;
                    });
                    
                    // Update the dog_run_order with dogs for this specific level
                    // IMPORTANT: For grouped classes, we need to preserve the inter-class positioning
                    // Sort the dogs by their position in the grouped class, not just their individual class order
                    const dogsWithGroupPosition = dogsForThisLevel.map(dog => {
                      const positionInGroup = cls.dogs.findIndex(groupDog => groupDog.scoreId === dog.scoreId);
                      return { ...dog, positionInGroup };
                    }).sort((a, b) => a.positionInGroup - b.positionInGroup);
                    
                    const newDogOrder = dogsWithGroupPosition.map(dog => dog.scoreId);
                    
                    dc.dog_run_order = newDogOrder;
                  }
                } else {

                }
              }
            }
          }
        }
      }

      // Save only the selected ring's running order via the ring-scoped endpoint.
      // Using updateShow here would (a) overwrite all other rings with a potentially
      // stale showRingsRef snapshot, and (b) trigger reconcileRunningOrderJudgesFromShowElements
      // on the server (because show_elements is included in showUpdateData), which
      // moves dog_classes between judge rows based on show_elements and can destroy
      // a manually-arranged cross-ring structure that differs from show_elements.
      const selectedRingRunningOrder = updatedRings[ringIndex].running_order;
      const updatedShow = await apiService.updateRingRunningOrder(
        event._id,
        show._id,
        selectedRing,
        selectedRingRunningOrder
      );

      // Update the stored rings ref with the latest data from the server
      if (updatedShow.rings && updatedShow.rings.length > 0) {
        showRingsRef.current = JSON.parse(JSON.stringify(updatedShow.rings));
      }

      onUpdate(updatedShow);

      // Notify JudgeSchedulesTab (via ManageShow) so it reloads entry counts for split tiles.
      onRunOrderSave?.();

      // Clear any previous errors on successful save
      if (error) {
        setError("");
      }
      
      // Success! Changes are now saved
    } catch (err) {
      // Check if this is a transient error that should be retried
      const isWriteConflict = err && typeof err === 'object' && err !== null && 
        (('code' in err && (err as any).code === 112) || // MongoDB WriteConflict
        ('message' in err && typeof (err as any).message === 'string' && (err as any).message.includes('Write conflict')));
      
      // Check if this is a Mongoose version error (optimistic concurrency control)
      const isVersionError = err && typeof err === 'object' && err !== null &&
        (('message' in err && typeof (err as any).message === 'string' && 
          (err as any).message.includes('No matching document found for id') &&
          (err as any).message.includes('version')) ||
        ('name' in err && (err as any).name === 'VersionError'));
      
      // For transient errors (write conflicts or version errors), retry automatically
      if ((isWriteConflict || isVersionError) && !saving) {
        setTimeout(() => {
          if (!saving) { // Double-check we're not already saving
            // For version errors, we need to reload the data first to get the latest version
            if (isVersionError) {
              loadRunOrderData().then(() => {
                setHasUnsavedChanges(true); // Keep unsaved state after reload
              });
            } else {
              // Just retry the save for write conflicts (will be handled by user clicking Save button)
              setHasUnsavedChanges(true);
            }
          }
        }, 1000);
        return; // Don't show error for transient conflicts, just retry
      }
      
      // Provide more specific error messages for other errors
      let errorMessage = "Failed to save run order. Please try again.";
      if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as any).response;
        if (response?.status === 400) {
          errorMessage = "Invalid data provided. Please check your entries and try again.";
        } else if (response?.status === 403) {
          errorMessage = "You don't have permission to modify this show.";
        } else if (response?.status === 404) {
          errorMessage = "Show not found. Please refresh the page and try again.";
        }
      }
      
      setError(errorMessage);
      // Keep hasUnsavedChanges true on error so user can retry
    } finally {
      setSaving(false);
    }
  }, [
    selectedRing, 
    runOrderClasses, 
    show.rings,
    show.show_name,
    show.show_type,
    show.show_date,
    show.fee,
    show.use_early_bird_fee,
    show.use_same_fee,
    show.use_registration_end_date,
    show.show_elements,
    show.club_name,
    show.show_club_id,
    show.event_number,
    show.trailing_show,
    show.previous_show_name,
    show.registration_end_date,
    show.early_bird_end_date,
    show.early_bird_fee,
    show.is_archived,
    show.max_dogs,
    show.first_run_time_minutes,
    show.run_duration_minutes,
    show.breed_judges,
    show.breed_group_judges,
    show.arm_number_start,
    show.specialty_breed,
    show.waitlist_option,
    show.waitlist_limit,
    show.waitlist_enabled,
    show.waitlist_size,
    event._id, 
    show._id, 
    onUpdate, 
    error,
    loadRunOrderData,
    saving,
    isBlindShow,
    blindSizes,
  ]);



  // Manual save handler - defined after saveRunOrder to avoid circular dependency
  const handleManualSave = useCallback(async () => {
    if (!hasUnsavedChanges || saving) return;
    
    await saveRunOrder();
    setHasUnsavedChanges(false);
  }, [hasUnsavedChanges, saving, saveRunOrder]);

  // Debounced save function - increased delay to prevent rapid clicks
  const debouncedSave = useMemo(
    () => debounce(saveRunOrder, 2000), // 2 second debounce for auto-save
    [saveRunOrder]
  );

  // Handle export — auto-save first so the generated document reflects any
  // unsaved drag/randomize/reverse changes made in this session.
  const handleExport = useCallback(async () => {
    if (!selectedRing) return;

    // Always save before export so the document reflects the current display order.
    // When dog_run_order is empty (never explicitly saved), saving persists the current
    // UI order (jump-height + armband sorted) so the forms-ms produces a matching document.
    await saveRunOrder();
    setHasUnsavedChanges(false);

    download.pushDownload({
      type: "run_order",
      eventId: event._id,
      showId: show._id,
      ring: selectedRing
    });
  }, [selectedRing, event._id, show._id, saveRunOrder]);

  // Handle manual retry
  const handleRetry = useCallback(() => {
    setError("");
    loadRunOrderData();
  }, [loadRunOrderData]);

  // ── Split helpers ──────────────────────────────────────────────────────────

  // Check if any class in the current ring is a split tile
  const hasSplitClasses = useMemo(
    () => runOrderClasses.some(c => c.split_number !== undefined),
    [runOrderClasses]
  );

  // For a given IRunOrderClass, find all sibling splits (same showElement+level+section, different split_number)
  const getSiblingClasses = useCallback((cls: IRunOrderClass): IRunOrderClass[] => {
    if (cls.split_number === undefined) return [];
    return runOrderClasses.filter(
      c =>
        c !== cls &&
        c.split_number !== undefined &&
        c.showElement === cls.showElement &&
        c.level === cls.level.replace(/ \(Split \d+\/\d+\)$/, '') &&
        c.section === cls.section
    );
  }, [runOrderClasses]);

  // Auto-distribute all dogs across all splits of a logical class (alternating assignment)
  const handleAutoDistribute = useCallback((sourceClassId: string) => {
    const source = runOrderClasses.find(c => c.id === sourceClassId);
    if (!source || source.split_number === undefined) return;

    // Find all sibling splits (including the source)
    const baseName = source.level.replace(/ \(Split \d+\/\d+\)$/, '');
    const allSplits = runOrderClasses
      .filter(c =>
        c.split_number !== undefined &&
        c.showElement === source.showElement &&
        c.level.replace(/ \(Split \d+\/\d+\)$/, '') === baseName &&
        c.section === source.section
      )
      .sort((a, b) => (a.split_number ?? 0) - (b.split_number ?? 0));

    if (allSplits.length < 2) return;

    // Pool all dogs from all splits
    const allDogs: IDogRunOrderEntry[] = [];
    allSplits.forEach(s => allDogs.push(...s.dogs));

    // Remove duplicates (keep first occurrence by scoreId)
    const seen = new Set<string>();
    const uniqueDogs = allDogs.filter(d => {
      if (seen.has(d.scoreId)) return false;
      seen.add(d.scoreId);
      return true;
    });

    // Distribute alternately across splits
    const distributed: IDogRunOrderEntry[][] = allSplits.map(() => []);
    uniqueDogs.forEach((dog, i) => {
      distributed[i % allSplits.length].push(dog);
    });

    const updated = runOrderClasses.map(c => {
      const splitIdx = allSplits.findIndex(s => s.id === c.id);
      if (splitIdx === -1) return c;
      return { ...c, dogs: distributed[splitIdx] };
    });

    setRunOrderClasses(updated);
    runOrderClassesRef.current = updated;
    const newGridData = convertToGridData(updated, scoresRef.current);
    setGridRowData(newGridData);
    setHasUnsavedChanges(true);
  }, [runOrderClasses, convertToGridData]);

  // Move a single dog from its current split to another split
  const handleMoveDogToSplit = useCallback((scoreId: string, fromClassId: string, toClassId: string) => {
    const current = runOrderClassesRef.current;
    const movingDog = current.find(c => c.id === fromClassId)?.dogs.find(d => d.scoreId === scoreId);
    if (!movingDog) return;

    const updated = current.map(c => {
      if (c.id === fromClassId) return { ...c, dogs: c.dogs.filter(d => d.scoreId !== scoreId) };
      if (c.id === toClassId) return { ...c, dogs: [...c.dogs, movingDog] };
      return c;
    });

    setRunOrderClasses(updated);
    runOrderClassesRef.current = updated;
    const newGridData = convertToGridData(updated, scoresRef.current);
    setGridRowData(newGridData);
    setHasUnsavedChanges(true);
  }, [convertToGridData]);

  // Keep the stable ref in sync
  useEffect(() => { moveDogToSplitRef.current = handleMoveDogToSplit; }, [handleMoveDogToSplit]);

  // Build AG Grid context menu items
  const getContextMenuItems = useCallback((params: any): any[] => {
    const row = params.node?.data as IGridRowData | undefined;
    if (!row) return ['copy'];

    // Find the class this row belongs to
    const cls = runOrderClasses.find(c => c.id === row.classId);
    if (!cls || cls.split_number === undefined) return ['copy'];

    const baseName = cls.level.replace(/ \(Split \d+\/\d+\)$/, '');
    const siblings = runOrderClasses.filter(
      c =>
        c !== cls &&
        c.split_number !== undefined &&
        c.showElement === cls.showElement &&
        c.level.replace(/ \(Split \d+\/\d+\)$/, '') === baseName &&
        c.section === cls.section
    );

    const allSplits = [cls, ...siblings].sort((a, b) => (a.split_number ?? 0) - (b.split_number ?? 0));

    const moveItems = siblings.map(sib => ({
      name: `Move to Split ${sib.split_number}`,
      action: () => handleMoveDogToSplit(row.scoreId, cls.id, sib.id),
    }));

    const autoDistItem = {
      name: `Auto-distribute ${allSplits.length} Splits`,
      action: () => handleAutoDistribute(cls.id),
    };

    return [
      ...(moveItems.length > 0 ? [{ name: 'Move to Split...', subMenu: moveItems }] : []),
      autoDistItem,
      'separator',
      'copy',
    ];
  }, [runOrderClasses, handleMoveDogToSplit, handleAutoDistribute]);

  // ── End Split helpers ──────────────────────────────────────────────────────

  // Determine what to display (for future use)
  // const displayClasses = runOrderClasses.length > 0 
  //   ? runOrderClasses 
  //   : runOrderClassesRef.current.length > 0 
  //     ? runOrderClassesRef.current 
  //     : isolatedDataRef.current;



  if (loading) {

    return (
      <div className={styles.RunOrderTab}>
        <div className={styles.header}>
          <div className={styles.controls}>
            <div className={styles.ringSelector}>
              <Dropdown
                label="Ring"
                placeholder="Select Ring"
                options={availableRings}
                value={availableRings.find(ring => ring.value === selectedRing) || availableRings[0]}
                onChange={handleRingChange}
              />
            </div>
          </div>
        </div>
        <div className={styles.loading}>
          <p>Loading run order data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.RunOrderTab}>
        <div className={styles.header}>
          <div className={styles.controls}>
            <div className={styles.ringSelector}>
              <Dropdown
                label="Ring"
                placeholder="Select Ring"
                options={availableRings}
                value={availableRings.find(ring => ring.value === selectedRing) || availableRings[0]}
                onChange={handleRingChange}
              />
            </div>
          </div>
        </div>
        <div className={styles.error}>
          <p>Something went wrong, click Retry to continue.</p>
          <Button onClick={handleRetry}>Retry</Button>
        </div>
      </div>
    );
  }

  if (gridRowData.length === 0) {
    
    return (
      <div className={styles.RunOrderTab}>
        <div className={styles.header}>
          <div className={styles.controls}>
            <div className={styles.ringSelector}>
              <Dropdown
                label="Ring"
                placeholder="Select Ring"
                options={availableRings}
                value={availableRings.find(ring => ring.value === selectedRing) || availableRings[0]}
                onChange={handleRingChange}
              />
            </div>
          </div>
        </div>
        <div className={styles.emptyState}>
          <p>No dogs are currently entered in any classes for this ring.</p>
          <p>Dogs will appear here once they are registered for classes in this show.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.RunOrderTab}>
      <div className={styles.header}>
        <div className={styles.topRow}>
          <div className={styles.ringSelector}>
            <Dropdown
              label="Ring"
              placeholder="Select Ring"
              options={availableRings}
              value={availableRings.find(ring => ring.value === selectedRing) || availableRings[0]}
              onChange={handleRingChange}
            />
          </div>
          <div className={styles.actionButtons}>
            {hasUnsavedChanges && (
              <Button 
                onClick={handleManualSave} 
                disabled={saving}
                className={styles.saveButton}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            )}
            {/* Randomize makes no sense when each dog picked its own time slot */}
            {!isTimeslotOrderedShow && (
              <Button onClick={handleRandomize} disabled={runOrderClasses.length === 0 || saving}>
                Randomize
              </Button>
            )}
            {shouldShowReverseOrder && (
              <Button onClick={handleReverseOrder} disabled={runOrderClasses.length === 0 || saving}>
                {isReversedOrder ? "Lowest to Highest" : "Highest to Lowest"}
              </Button>
            )}
            {hasSplitClasses && (
              <Button
                onClick={() => {
                  // Auto-distribute the first split class found
                  const firstSplit = runOrderClasses.find(c => c.split_number === 1);
                  if (firstSplit) handleAutoDistribute(firstSplit.id);
                }}
                disabled={saving}
                title="Evenly redistribute dogs across all split classes"
              >
                Auto-distribute Splits
              </Button>
            )}
            <Button onClick={handleReset} disabled={saving}>
              Reset to Default
            </Button>
            <Button onClick={handleExport} disabled={runOrderClasses.length === 0}>
              Export
            </Button>
          </div>
        </div>
        <div className={styles.bottomRow}>
          {selectedRing && runOrderClasses.length > 0 && (
            <div className={styles.progressInfo}>
              <div>
                Progress: {gridRowData.filter(row => row.checkedIn).length} checked in, {gridRowData.filter(row => row.completed).length} completed of {gridRowData.length} runs
                {(() => {
                  const completed = gridRowData.filter(row => row.completed).length;
                  const total = gridRowData.length;
                return completed === total && total > 0 ? (
                  <span className={styles.completedIcon}>✓</span>
                ) : null;
              })()}
              </div>
              <div className={styles.legend}>
                <span className={styles.legendItem}>
                  <span className={styles.checkedInIcon}>✓</span> Checked In
                </span>
                <span className={styles.legendItem}>
                  <span className={styles.completedIcon}>✓</span> Complete
                </span>
              </div>
            </div>
          )}
          <div className={styles.showRunOrderToggle}>
            <Checkbox
              id="show-run-order-checkbox"
              label="Show run order to dog owners"
              value={show.show_run_order || false}
              onChange={handleShowRunOrderToggle}
            />
          </div>
        </div>
      </div>

      {/* Judge Schedule Changes Alert Banner */}
      {judgeScheduleChangesPending && (
        <div className={styles.alertBanner}>
          <div className={styles.alertContent}>
            <span className={styles.alertText}>
              Your Judging Schedule has changed. Update your run order to reflect the most recent changes.
            </span>
            <Button 
              onClick={onManualSync}
              className={styles.updateButton}
            >
              Update
            </Button>
          </div>
        </div>
      )}

      {/* Blind size controls for BHA Barn Hunt / ABI URR */}
      {isBlindShow && runOrderClasses.length > 0 && (
        <div className={styles.blindPanel}>
          <div
            className={styles.blindPanelHeader}
            onClick={() => setBlindPanelCollapsed((c) => !c)}
          >
            <span>Blind Sizes</span>
            <button type="button" aria-label={blindPanelCollapsed ? 'Expand' : 'Collapse'}>
              {blindPanelCollapsed ? 'Show ▸' : 'Hide ▾'}
            </button>
          </div>
          {!blindPanelCollapsed && (
            <div className={styles.blindPanelBody}>
              {runOrderClasses.map((cls) => {
                const isABISDG = event.sanctioning_club === Constants.sanctioning_club.ABI &&
                  show.show_type === Constants.show_type.scent_dog_games;
                const label = isABISDG
                  ? [cls.level, cls.section].filter(Boolean).join(' - ')
                  : [cls.showElement, cls.level, cls.section ? `(${cls.section})` : ''].filter(Boolean).join(' - ');
                return (
                  <div key={cls.id} className={styles.blindSizeItem}>
                    <label title={label}>{label}</label>
                    <input
                      type="number"
                      min={1}
                      max={99}
                      value={blindSizes.get(cls.id) ?? getDefaultBlindSize(cls.level, show.show_type)}
                      onChange={(e) =>
                        handleBlindSizeChange(cls.id, parseInt(e.target.value) || 1)
                      }
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className={styles.gridContainer}>
        <div className="ag-theme-quartz" style={{ height: '500px' }}>
          <AgGridReact
            ref={gridApiRef}
            theme={theme}
            rowData={gridRowDataWithBlinds}
            columnDefs={columnDefs}
            rowClassRules={{
              [styles.completedRow]: (params) =>
                !!params.data && !params.node.group && !!params.data.completed,
            }}
            rowDragManaged={false}
            onRowDragEnd={event.sanctioning_club === Constants.sanctioning_club.AKC && show.show_type === Constants.show_type.conformation && !show.specialty_breed ? undefined : onRowDragEnd}
            onGridReady={(params) => {

            }}
            animateRows={true}
            suppressRowClickSelection={true}
            getRowId={(params) => params.data.id}
            suppressMovableColumns={true}
            rowHeight={45}
            headerHeight={45}
            groupDisplayType="groupRows"
            groupDefaultExpanded={-1}
            suppressAggFuncInHeader={true}
            autoGroupColumnDef={{
              headerName: event.sanctioning_club === Constants.sanctioning_club.AKC && show.show_type === Constants.show_type.fast_cat 
                ? "Timeslot" 
                : event.sanctioning_club === Constants.sanctioning_club.AKC && show.show_type === Constants.show_type.conformation 
                ? "Judge" 
                : "Class / Judge",
              minWidth: 400,
              cellRendererParams: {
                suppressCount: true,
                suppressDoubleClickExpand: false,
                suppressEnterExpand: false
              }
            }}
            defaultColDef={{
              resizable: true,
              sortable: false,
              filter: false
            }}
            suppressContextMenu={true}
          />
        </div>
      </div>
    </div>
  );
});

export default RunOrderTab;